import { useMasterProductList } from "@/api/services/admin/master-data/product";
import { TGetMasterProductListItemDto } from "@/api/services/admin/master-data/product/dtos/get-master-product-list.dto";
import { IconChevronRight } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { EllipsisText } from "@/components/elements/EllipsisText";
import { IconBox } from "@/components/elements/IconBox";
import { Linkable } from "@/components/elements/Linkable";
import { Table } from "@/components/elements/Table";
import { DATE_TIME_FORMAT } from "@/constants/datetime";
import { EMPTY_STR } from "@/constants/string";
import { MASTER_DATA_TYPE, MASTER_DATA_TYPE_TITLE } from "@/features/master-data/constants/masterDataType";
import { usePagination } from "@/hooks/usePagination";
import { useTable } from "@/hooks/useTable";
import { AppRoutes } from "@/routes/config";
import { dayjs } from "@/utils/dayjs";
import { getDynamicRoute } from "@/utils/url";
import { Box, Pagination as MuiPagination, Stack, Typography } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

const UNPUBLISHED_LABEL = "未定";

export const MasterDataProduct = () => {
  const navigate = useNavigate();
  const pagReq = usePagination();
  const { data, isLoading, isFetching } = useMasterProductList(pagReq);
  const items = data?.data ?? [];
  const pag = data?.pagination;

  const [searchParams, setSearchParams] = useSearchParams();

  const columns = useMemo<MRT_ColumnDef<TGetMasterProductListItemDto>[]>(
    () => [
      {
        header: "プロダクト名",
        Cell: ({ row }) => <EllipsisText title={row.original.name || EMPTY_STR.TEXT} variant="body14" maxWidth={360} />,
      },
      {
        header: "1文字表記",
        Cell: ({ row }) => row.original.singleCharLabel || EMPTY_STR.TEXT,
        size: 100,
      },
      {
        header: "公開日時",
        Cell: ({ row }) => (row.original.publishedAt ? dayjs(row.original.publishedAt).format(DATE_TIME_FORMAT) : UNPUBLISHED_LABEL),
        size: 160,
      },
      {
        header: "更新日",
        Cell: ({ row }) => (row.original.updatedAt ? dayjs(row.original.updatedAt).format(DATE_TIME_FORMAT) : EMPTY_STR.DATE_TIME),
        size: 160,
      },
      {
        header: "詳細",
        id: "action",
        size: 20,
        Cell: ({ row }) => (
          <Linkable href={getDynamicRoute(AppRoutes.masterDataProductEdit, { productId: row.original.id })}>
            <IconBox>
              <IconChevronRight />
            </IconBox>
          </Linkable>
        ),
      },
    ],
    [],
  );

  const { table } = useTable({
    columns,
    rows: items,
    onClickRow: (row) => navigate(getDynamicRoute(AppRoutes.masterDataProductEdit, { productId: row.id })),
    pinningColumns: ["action"],
  });

  const handleChangePage = (_: unknown, page: number) => {
    const next = new URLSearchParams(searchParams);
    next.set("page", String(page));
    setSearchParams(next);
  };

  return (
    <>
      <Box mt="78px" mb="24px">
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="head32Bold">{MASTER_DATA_TYPE_TITLE[MASTER_DATA_TYPE.PRODUCT]}</Typography>
          <Button size="sm" onClick={() => navigate(AppRoutes.masterDataProductCreate)}>
            プロダクトを追加
          </Button>
        </Stack>
      </Box>
      <Table table={table} isLoading={isLoading} isFetching={isFetching} />
      {pag && pag.totalPage > 1 && (
        <Stack direction="row" justifyContent="center" mt={3}>
          <MuiPagination page={pag.page} count={pag.totalPage} shape="rounded" onChange={handleChangePage} />
        </Stack>
      )}
    </>
  );
};
